import axios from "axios";
import config from "../../config";

/**
 * Public checkout-page endpoints on the membership admin app (same host as the
 * sales /cs data), no auth. `fetchCheckout` returns the theme + course + price +
 * pricing plans + enabled gateways the CheckoutTemplate components render;
 * `cartCheck` recomputes totals for a plan + coupon; `createOrder` validates and
 * creates the order. Mirrors the FlexiFunnels checkout contract, course-scoped.
 */
const client = axios.create({ baseURL: config.LANDING_API_URL });

export async function fetchCheckout(code) {
  const res = await client.get(`/cs/${encodeURIComponent(code)}/checkout`);
  return res.data;
}

/** Recompute totals for a selected pricing plan + optional coupon. */
export async function cartCheck(code, payload) {
  const res = await client.post(`/cs/${encodeURIComponent(code)}/cart-check`, payload);
  return res.data;
}

/** Validate the buyer form, create the order, and start payment. */
export async function createOrder(code, payload) {
  const res = await client.post(`/cs/${encodeURIComponent(code)}/order`, payload);
  return res.data;
}

/** Poll an order's settle state — body.status === 3 means paid. */
export async function orderStatus(code, paymentId) {
  const res = await client.post(`/cs/${encodeURIComponent(code)}/order-status`, { payment_id: paymentId });
  return res.data;
}

/** Finalise a paid order (grant + credentials) and get the thank-you redirect. */
export async function handlePaymentResponse(code, paymentId) {
  const res = await client.post(`/cs/${encodeURIComponent(code)}/handle-payment-response`, { payment_id: paymentId });
  return res.data;
}

/**
 * Drive a redirect/SDK gateway to completion: poll order-status until paid
 * (status 3), then call handle-payment-response for the final redirect. Mirrors
 * ft-checkout.js's orderverifier loop. Returns the thank-you URL, or null on
 * timeout. `intervalMs`/`tries` bound the wait (default ~2min).
 */
export async function pollUntilPaid(code, paymentId, { intervalMs = 5000, tries = 24 } = {}) {
  for (let i = 0; i < tries; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    const s = await orderStatus(code, paymentId);
    if (Number(s?.body?.status) === 3) {
      // eslint-disable-next-line no-await-in-loop
      const done = await handlePaymentResponse(code, paymentId);
      return done?.body?.redirect || s?.body?.redirect || null;
    }
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return null;
}

/** Post-purchase summary + login credentials, by order token (payment uuid). */
export async function fetchThankyou(code, order) {
  const res = await client.get(`/cs/${encodeURIComponent(code)}/thankyou`, { params: { order } });
  return res.data;
}
